import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Clock, Download, Calendar, User, DollarSign, Plus, Edit2, Trash2, X, 
  FileText, Filter, RefreshCw, AlertCircle, Check, Briefcase
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminHoursReport = ({ token, language, staff = [], appointments = [] }) => {
  const [report, setReport] = useState(null);
  const [workSessions, setWorkSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Ensure staff and appointments are arrays
  const staffList = Array.isArray(staff) ? staff : [];
  const appointmentsList = Array.isArray(appointments) ? appointments : [];
  
  // Manual time entry modal
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualEntry, setManualEntry] = useState({
    job_id: '',
    staff_id: '',
    hours: 0,
    minutes: 0,
    notes: '',
    work_date: new Date().toISOString().split('T')[0]
  });
  
  // Filter jobs by selected staff - show only jobs assigned to this staff member
  const filteredJobsForStaff = useMemo(() => {
    if (!manualEntry.staff_id) {
      // If no staff selected, show all jobs
      return appointmentsList;
    }
    
    // Filter appointments where staff is assigned
    return appointmentsList.filter(apt => {
      // Check assigned_staff_id (single assignment - legacy)
      if (apt.assigned_staff_id === manualEntry.staff_id) return true;
      
      // Check assigned_staff_ids array (multi-staff assignment)
      if (Array.isArray(apt.assigned_staff_ids) && apt.assigned_staff_ids.includes(manualEntry.staff_id)) return true;
      
      // Check assigned_staff array of objects [{staff_id, name, is_leader}]
      if (Array.isArray(apt.assigned_staff)) {
        return apt.assigned_staff.some(s => s.staff_id === manualEntry.staff_id);
      }
      
      return false;
    });
  }, [manualEntry.staff_id, appointmentsList]);
  
  // Reset job selection when staff changes
  const handleStaffChange = (staffId) => {
    setManualEntry(prev => ({ 
      ...prev, 
      staff_id: staffId,
      job_id: '' // Reset job when staff changes
    }));
  };
  
  // Edit session modal
  const [editingSession, setEditingSession] = useState(null);
  const [editHours, setEditHours] = useState(0);
  const [editMinutes, setEditMinutes] = useState(0);
  const [editNotes, setEditNotes] = useState('');

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/admin/hours-report?period=${period}`;
      if (selectedStaff) url += `&staff_id=${selectedStaff}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(res.data);
    } catch (err) {
      console.error('Failed to fetch hours report:', err);
      setError(language === 'pt' ? 'Erro ao carregar relatório' : 'Error loading report');
    } finally {
      setLoading(false);
    }
  }, [token, period, selectedStaff, language]);

  const fetchWorkSessions = useCallback(async () => {
    try {
      let url = `${API}/admin/work-sessions`;
      if (selectedStaff) url += `?staff_id=${selectedStaff}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkSessions(res.data.sessions || []);
    } catch (err) {
      console.error('Failed to fetch work sessions:', err);
    }
  }, [token, selectedStaff]);

  useEffect(() => {
    fetchReport();
    fetchWorkSessions();
  }, [fetchReport, fetchWorkSessions]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hrs}h ${mins}m`;
  };

  const handleManualEntry = async () => {
    try {
      const totalSeconds = (parseInt(manualEntry.hours) || 0) * 3600 + (parseInt(manualEntry.minutes) || 0) * 60;
      
      if (totalSeconds === 0) {
        setError(language === 'pt' ? 'Insira um tempo válido' : 'Enter a valid time');
        return;
      }
      
      await axios.post(`${API}/admin/work-sessions/manual`, {
        job_id: manualEntry.job_id,
        job_type: 'appointment',
        staff_id: manualEntry.staff_id,
        elapsed_seconds: totalSeconds,
        notes: manualEntry.notes,
        work_date: manualEntry.work_date
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(language === 'pt' ? 'Tempo adicionado com sucesso!' : 'Time added successfully!');
      setShowManualModal(false);
      setManualEntry({ job_id: '', staff_id: '', hours: 0, minutes: 0, notes: '', work_date: new Date().toISOString().split('T')[0] });
      fetchReport();
      fetchWorkSessions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'pt' ? 'Erro ao adicionar tempo' : 'Error adding time'));
    }
  };

  const handleEditSession = async () => {
    try {
      const totalSeconds = (parseInt(editHours) || 0) * 3600 + (parseInt(editMinutes) || 0) * 60;
      
      await axios.patch(
        `${API}/admin/work-sessions/${editingSession.id}?elapsed_seconds=${totalSeconds}&notes=${encodeURIComponent(editNotes)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(language === 'pt' ? 'Tempo atualizado!' : 'Time updated!');
      setEditingSession(null);
      fetchReport();
      fetchWorkSessions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'pt' ? 'Erro ao atualizar' : 'Error updating'));
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm(language === 'pt' ? 'Eliminar esta sessão de trabalho?' : 'Delete this work session?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/work-sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(language === 'pt' ? 'Sessão eliminada!' : 'Session deleted!');
      fetchReport();
      fetchWorkSessions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'pt' ? 'Erro ao eliminar' : 'Error deleting'));
    }
  };

  const openEditModal = (session) => {
    setEditingSession(session);
    setEditHours(Math.floor(session.elapsed_seconds / 3600));
    setEditMinutes(Math.floor((session.elapsed_seconds % 3600) / 60));
    setEditNotes(session.notes || '');
  };

  const exportToPDF = () => {
    if (!report) return;
    
    // Create printable content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório de Horas - Sancur</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
          h1 { color: #1a5c3a; border-bottom: 2px solid #1a5c3a; padding-bottom: 10px; }
          h2 { color: #333; margin-top: 30px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background-color: #1a5c3a; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .summary { background: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .summary-item { display: inline-block; margin-right: 40px; }
          .summary-value { font-size: 24px; font-weight: bold; color: #1a5c3a; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>Sancur Property Services</h1>
        <h2>Relatório de Horas Trabalhadas</h2>
        
        <div class="summary">
          <div class="summary-item">
            <div>Período</div>
            <div class="summary-value">${period === 'day' ? 'Hoje' : period === 'week' ? '7 Dias' : period === 'month' ? '30 Dias' : 'Total'}</div>
          </div>
          <div class="summary-item">
            <div>Total Horas</div>
            <div class="summary-value">${report.total_hours}h</div>
          </div>
          <div class="summary-item">
            <div>Trabalhos Concluídos</div>
            <div class="summary-value">${report.total_jobs}</div>
          </div>
          <div class="summary-item">
            <div>Funcionários</div>
            <div class="summary-value">${report.total_staff}</div>
          </div>
        </div>
        
        <h2>Detalhes por Funcionário</h2>
        <table>
          <thead>
            <tr>
              <th>Funcionário</th>
              <th>Horas Trabalhadas</th>
              <th>Trabalhos</th>
              <th>Taxa/Hora</th>
              <th>Ganhos Estimados</th>
            </tr>
          </thead>
          <tbody>
            ${report.staff_reports.map(r => `
              <tr>
                <td>${r.staff_name}</td>
                <td>${r.total_hours}h</td>
                <td>${r.jobs_completed}</td>
                <td>€${r.hourly_rate.toFixed(2)}</td>
                <td><strong>€${r.estimated_earnings.toFixed(2)}</strong></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="footer">
          Gerado em: ${new Date().toLocaleString('pt-PT')}
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const getJobName = (jobId) => {
    const job = appointmentsList.find(a => a.id === jobId);
    return job?.name || jobId.substring(0, 8) + '...';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
          <button onClick={() => setError('')} className="ml-auto"><X size={18} /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg flex items-center gap-2">
          <Check size={20} />
          {success}
        </div>
      )}

      {/* Header with filters */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
          <Clock className="text-primary" size={24} />
          {language === 'pt' ? 'Relatório de Horas' : 'Hours Report'}
        </h2>
        
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Period filter */}
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary text-sm sm:text-base min-w-[100px]"
          >
            <option value="day">{language === 'pt' ? 'Hoje' : 'Today'}</option>
            <option value="week">{language === 'pt' ? '7 Dias' : '7 Days'}</option>
            <option value="month">{language === 'pt' ? '30 Dias' : '30 Days'}</option>
            <option value="all">{language === 'pt' ? 'Tudo' : 'All'}</option>
          </select>
          
          {/* Staff filter */}
          <select
            value={selectedStaff}
            onChange={(e) => setSelectedStaff(e.target.value)}
            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary text-sm sm:text-base min-w-[120px]"
          >
            <option value="">{language === 'pt' ? 'Todos' : 'All Staff'}</option>
            {staffList.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          
          {/* Mobile: Stack buttons, Desktop: Inline */}
          <div className="flex gap-2 w-full sm:w-auto">
            {/* Add manual time */}
            <button
              onClick={() => setShowManualModal(true)}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
            >
              <Plus size={18} />
              <span className="hidden xs:inline">{language === 'pt' ? 'Adicionar' : 'Add'}</span>
              <span className="xs:hidden">{language === 'pt' ? '+' : '+'}</span>
            </button>
            
            {/* Export PDF */}
            <button
              onClick={exportToPDF}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm sm:text-base"
            >
              <Download size={18} />
              <span className="hidden xs:inline">{language === 'pt' ? 'PDF' : 'PDF'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {report && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm border">
            <div className="flex items-center gap-1 sm:gap-2 text-gray-500 text-xs sm:text-sm mb-1">
              <Clock size={14} className="sm:w-4 sm:h-4" />
              {language === 'pt' ? 'Total Horas' : 'Total Hours'}
            </div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{report.total_hours}h</div>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm border">
            <div className="flex items-center gap-1 sm:gap-2 text-gray-500 text-xs sm:text-sm mb-1">
              <FileText size={14} className="sm:w-4 sm:h-4" />
              {language === 'pt' ? 'Trabalhos' : 'Jobs'}
            </div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{report.total_jobs}</div>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm border">
            <div className="flex items-center gap-1 sm:gap-2 text-gray-500 text-xs sm:text-sm mb-1">
              <User size={14} className="sm:w-4 sm:h-4" />
              {language === 'pt' ? 'Funcionários' : 'Staff'}
            </div>
            <div className="text-xl sm:text-2xl font-bold text-gray-900">{report.total_staff}</div>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-xl shadow-sm border">
            <div className="flex items-center gap-1 sm:gap-2 text-gray-500 text-xs sm:text-sm mb-1">
              <DollarSign size={14} className="sm:w-4 sm:h-4" />
              {language === 'pt' ? 'Ganhos' : 'Earnings'}
            </div>
            <div className="text-xl sm:text-2xl font-bold text-primary">
              €{report.staff_reports.reduce((sum, r) => sum + r.estimated_earnings, 0).toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Staff breakdown - Mobile card view on small screens, table on larger */}
      {report && report.staff_reports.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="p-3 sm:p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
              {language === 'pt' ? 'Detalhes por Funcionário' : 'Staff Breakdown'}
            </h3>
          </div>
          
          {/* Mobile view - Cards */}
          <div className="sm:hidden divide-y">
            {report.staff_reports.map((r, idx) => (
              <div key={idx} className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                    {r.staff_name.charAt(0)}
                  </div>
                  <span className="font-medium text-sm">{r.staff_name}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">{language === 'pt' ? 'Horas' : 'Hours'}: </span>
                    <span className="font-medium">{r.total_hours}h</span>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">{language === 'pt' ? 'Trabalhos' : 'Jobs'}: </span>
                    <span className="font-medium">{r.jobs_completed}</span>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="text-gray-500">{language === 'pt' ? 'Taxa' : 'Rate'}: </span>
                    <span className="font-medium">€{r.hourly_rate.toFixed(2)}/h</span>
                  </div>
                  <div className="bg-primary/10 p-2 rounded">
                    <span className="text-gray-500">{language === 'pt' ? 'Ganhos' : 'Earnings'}: </span>
                    <span className="font-bold text-primary">€{r.estimated_earnings.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Desktop view - Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    {language === 'pt' ? 'Funcionário' : 'Staff'}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    {language === 'pt' ? 'Horas' : 'Hours'}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    {language === 'pt' ? 'Trabalhos' : 'Jobs'}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    {language === 'pt' ? 'Taxa/Hora' : 'Rate/Hour'}
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">
                    {language === 'pt' ? 'Ganhos' : 'Earnings'}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {report.staff_reports.map((r, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                          {r.staff_name.charAt(0)}
                        </div>
                        <span className="font-medium">{r.staff_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium">{r.total_hours}h</td>
                    <td className="px-4 py-3">{r.jobs_completed}</td>
                    <td className="px-4 py-3">€{r.hourly_rate.toFixed(2)}</td>
                    <td className="px-4 py-3 font-bold text-primary">€{r.estimated_earnings.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Work Sessions History */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="p-3 sm:p-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
            {language === 'pt' ? 'Histórico de Sessões' : 'Session History'}
          </h3>
        </div>
        
        {workSessions.length === 0 ? (
          <p className="p-4 sm:p-6 text-center text-gray-500 text-sm">
            {language === 'pt' ? 'Nenhuma sessão registada' : 'No sessions recorded'}
          </p>
        ) : (
          <div className="divide-y max-h-96 overflow-y-auto">
            {workSessions.filter(s => s.action === 'finish').slice(0, 50).map((session) => (
              <div key={session.id} className="p-3 sm:p-4 hover:bg-gray-50">
                {/* Mobile Layout */}
                <div className="sm:hidden">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        session.is_manual ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {session.is_manual ? <Edit2 size={14} /> : <Clock size={14} />}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{session.staff_name}</p>
                        <p className="text-xs text-gray-500">{new Date(session.timestamp).toLocaleDateString('pt-PT')}</p>
                      </div>
                    </div>
                    <span className="text-base font-bold text-gray-900">
                      {formatTime(session.elapsed_seconds)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2 truncate">{getJobName(session.job_id)}</p>
                  {session.notes && (
                    <p className="text-xs text-gray-400 mb-2 line-clamp-2">{session.notes}</p>
                  )}
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => openEditModal(session)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteSession(session.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                
                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      session.is_manual ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {session.is_manual ? <Edit2 size={18} /> : <Clock size={18} />}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{session.staff_name}</p>
                      <p className="text-sm text-gray-500">
                        {getJobName(session.job_id)} • {new Date(session.timestamp).toLocaleDateString('pt-PT')}
                      </p>
                      {session.notes && (
                        <p className="text-xs text-gray-400 mt-1">{session.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-gray-900">
                      {formatTime(session.elapsed_seconds)}
                    </span>
                    <button
                      onClick={() => openEditModal(session)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={language === 'pt' ? 'Editar' : 'Edit'}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteSession(session.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={language === 'pt' ? 'Eliminar' : 'Delete'}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manual Entry Modal */}
      {showManualModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                {language === 'pt' ? 'Adicionar Tempo Manual' : 'Add Manual Time'}
              </h3>
              <button onClick={() => setShowManualModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Funcionário' : 'Staff'}
                </label>
                <select
                  data-testid="manual-time-staff-select"
                  value={manualEntry.staff_id}
                  onChange={(e) => handleStaffChange(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                >
                  <option value="">{language === 'pt' ? 'Selecionar...' : 'Select...'}</option>
                  {staffList.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <Briefcase size={16} />
                  {language === 'pt' ? 'Trabalho' : 'Job'}
                  {manualEntry.staff_id && filteredJobsForStaff.length > 0 && (
                    <span className="text-xs text-green-600 font-normal">
                      ({filteredJobsForStaff.length} {language === 'pt' ? 'atribuído(s)' : 'assigned'})
                    </span>
                  )}
                </label>
                <select
                  data-testid="manual-time-job-select"
                  value={manualEntry.job_id}
                  onChange={(e) => setManualEntry(prev => ({ ...prev, job_id: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                  disabled={!manualEntry.staff_id}
                >
                  <option value="">
                    {!manualEntry.staff_id 
                      ? (language === 'pt' ? 'Selecione primeiro o funcionário' : 'Select staff first')
                      : filteredJobsForStaff.length === 0
                        ? (language === 'pt' ? 'Nenhum trabalho atribuído' : 'No jobs assigned')
                        : (language === 'pt' ? 'Selecionar trabalho...' : 'Select job...')
                    }
                  </option>
                  {filteredJobsForStaff.map(a => (
                    <option key={a.id} value={a.id}>{a.name} - {a.service_type}</option>
                  ))}
                  {/* Option to show all jobs if needed */}
                  {manualEntry.staff_id && filteredJobsForStaff.length === 0 && appointmentsList.length > 0 && (
                    <optgroup label={language === 'pt' ? '── Todos os trabalhos ──' : '── All jobs ──'}>
                      {appointmentsList.map(a => (
                        <option key={`all-${a.id}`} value={a.id}>{a.name} - {a.service_type}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {manualEntry.staff_id && filteredJobsForStaff.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {language === 'pt' 
                      ? '⚠️ Este funcionário não tem trabalhos atribuídos. A mostrar todos os trabalhos disponíveis.'
                      : '⚠️ This staff has no assigned jobs. Showing all available jobs.'}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Tempo Trabalhado' : 'Time Worked'}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={manualEntry.hours}
                    onChange={(e) => setManualEntry(prev => ({ ...prev, hours: e.target.value }))}
                    className="w-20 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary text-center"
                    placeholder="0"
                  />
                  <span className="text-gray-500">h</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={manualEntry.minutes}
                    onChange={(e) => setManualEntry(prev => ({ ...prev, minutes: e.target.value }))}
                    className="w-20 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary text-center"
                    placeholder="0"
                  />
                  <span className="text-gray-500">m</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Data do Trabalho' : 'Work Date'}
                </label>
                <input
                  type="date"
                  value={manualEntry.work_date}
                  onChange={(e) => setManualEntry(prev => ({ ...prev, work_date: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Notas' : 'Notes'}
                </label>
                <textarea
                  value={manualEntry.notes}
                  onChange={(e) => setManualEntry(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                  rows={2}
                  placeholder={language === 'pt' ? 'Motivo da adição manual...' : 'Reason for manual entry...'}
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowManualModal(false)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                {language === 'pt' ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={handleManualEntry}
                disabled={!manualEntry.staff_id || !manualEntry.job_id}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {language === 'pt' ? 'Adicionar' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Session Modal */}
      {editingSession && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                {language === 'pt' ? 'Editar Tempo' : 'Edit Time'}
              </h3>
              <button onClick={() => setEditingSession(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {editingSession.staff_name} - {getJobName(editingSession.job_id)}
              </p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Tempo' : 'Time'}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={editHours}
                    onChange={(e) => setEditHours(e.target.value)}
                    className="w-20 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary text-center"
                  />
                  <span className="text-gray-500">h</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={editMinutes}
                    onChange={(e) => setEditMinutes(e.target.value)}
                    className="w-20 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary text-center"
                  />
                  <span className="text-gray-500">m</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'pt' ? 'Notas' : 'Notes'}
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditingSession(null)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                {language === 'pt' ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={handleEditSession}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                {language === 'pt' ? 'Guardar' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHoursReport;
